import type * as React from "react";
import { type FC, useState } from "react";
import { type FieldValidationResults, sendUpdatePasswordRequest } from "~/features/auth/login/api/UpdatePassword";
import { redirectToUrl } from "~/utils/url/urlUtils";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";

import { Button, Stack, TextField, styled } from "~/common/mui";
import ToastMessage from "~/common/components/Messages/ToastMessage/ToastMessage";
import { useInputState } from "~/common/hooks/useInputState";
import { Card } from "~/features/configurator/Linear/components/Card";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";

interface Props {
  isNewUser: boolean;
  expirationDays: number;
  token: string;
}

const PASSWORD_FIELD_NAME = "pwd";
const CHECK_PASSWORD_FIELD_NAME = "checkPwd";

const checkAndApplyError = (
  validationResults: FieldValidationResults,
  fieldName: string,
  setError: (err: string) => void,
) => {
  const fieldErrors = validationResults[fieldName];
  if (fieldErrors) {
    setError(fieldErrors[0] || "");
  }
};

export const ChangePasswordPage: FC<Props> = (props) => {
  const { isNewUser } = props;
  const { t } = useTranslation();

  const pageTitle = t(`text.account.profile.${isNewUser ? "setPassword" : "resetPassword"}`);
  const btnLabel = t(`updatePwd.${isNewUser ? "confirm" : "submit"}`);

  const [pwd, setPwd] = useInputState("");
  const [pwdError, setPwdError] = useState("");
  const [checkPwd, setCheckPwd] = useInputState("");
  const [checkPwdError, setCheckPwdError] = useState("");
  const [globalErrors, setGlobalErrors] = useState([] as string[]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError("");
    setCheckPwdError("");

    sendUpdatePasswordRequest({
      pwd,
      checkPwd,
      token: props.token,
      newCustomer: props.isNewUser,
    }).then((result) => {
      if (Object.keys(result.validationResults).length > 0) {
        checkAndApplyError(result.validationResults, PASSWORD_FIELD_NAME, setPwdError);
        checkAndApplyError(result.validationResults, CHECK_PASSWORD_FIELD_NAME, setCheckPwdError);
      } else if (result.globalErrors.length > 0) {
        setGlobalErrors(result.globalErrors);
      } else {
        redirectToUrl(HOMEPAGE + "?login=true");
      }
    });
  };

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);
  const deviceClass = "reset-password-" + (isDesktop ? "desktop" : "mobile");

  return (
    <ResetPasswordRoot className={"scha__reset-password " + deviceClass}>
      <PageTitle title={pageTitle} />
      <Card className={isDesktop ? "p-3" : "card"} disableHover={true}>
        <form onSubmit={onSubmit}>
          <div className="row">
            <div className="col-md-6">
              <TextField
                fullWidth
                name={PASSWORD_FIELD_NAME}
                label={t("updatePwd.pwd.label")}
                inputProps={{ "data-test-id": "change-password-input" }}
                type={"password"}
                value={pwd}
                onChange={setPwd}
                error={!!pwdError}
                helperText={pwdError ? t(pwdError) : undefined}
              />
            </div>
            <div className="col-md-6">
              <TextField
                fullWidth
                name={CHECK_PASSWORD_FIELD_NAME}
                label={t("updatePwd.checkPwd.label")}
                inputProps={{ "data-test-id": "change-repeat-password-input" }}
                type={"password"}
                value={checkPwd}
                onChange={setCheckPwd}
                error={!!checkPwdError}
                helperText={checkPwdError ? t(checkPwdError) : undefined}
              />
            </div>
          </div>

          <Alert severity={"info"} className={"mt-3 mb-30"}>
            <p>{t("updatePwd.form.pwd.info")}</p>
            <p>
              {t("updatePwd.form.pwd.expiration", {
                days: props.expirationDays,
              })}
            </p>
          </Alert>

          <div className="d-flex flex-justify-end actions">
            <Stack gap={4}>
              <Button variant={"text"} data-test-id={"change-cancel-btn"} href={HOMEPAGE}>
                {t("cancel")}
              </Button>

              <Button data-test-id={"change-submit-btn"} type={"submit"}>
                {btnLabel}
              </Button>
            </Stack>
          </div>
        </form>
      </Card>

      {globalErrors.map((globalError) => (
        <ToastMessage type={"error"} key={globalError}>
          {t(globalError)}
        </ToastMessage>
      ))}
    </ResetPasswordRoot>
  );
};

export function createChangePasswordPage(props: Props) {
  return <ChangePasswordPage {...props} />;
}

const ResetPasswordRoot = styled("div")(({ theme }) => ({
  "&.reset-password-mobile .card": {
    padding: theme.spacing(1.5),
    "& .row": {
      margin: 0,
    },
  },
  "&.reset-password-mobile .actions > *": {
    flexDirection: "column-reverse",
    flexGrow: 1,
    "& button": { margin: 0 },
    "& a": { margin: `${theme.spacing(1)} 0 0` },
  },
}));
